import { createExternalActorId } from "@brains/contracts";
import {
  buildMessageActorMetadata,
  buildMessageSourceMetadata,
  buildResponsePlan,
  collectDeniedArtifactCardIds,
  redactUploadRefs,
  type AgentNamespace,
  type AgentResponse,
  type ChatAttachment,
  type ChatContext,
  type MessageArtifactEntity,
  type UserPermissionLevel,
} from "@brains/plugins";
import type { ApprovalResponse } from "./chat-input";
import { stripInternalEntityMemoryNote } from "./display-content";
import {
  writePlanCards,
  writeTextPart,
  type StreamWriter,
} from "./stream-writer";

export interface ActiveStream {
  writer: StreamWriter;
}

interface StreamDeps {
  activeStreams: Map<string, ActiveStream>;
  /** The two calls this stream makes, not the whole agent namespace. */
  agent: Pick<AgentNamespace, "chat" | "confirmPendingAction">;
  startProcessingInput(conversationId: string): void;
  endProcessingInput(): void;
  handleAgentResponseToolStatuses(
    response: Pick<AgentResponse, "cards" | "pendingConfirmations">,
    conversationId: string,
  ): Promise<void>;
  createId(prefix: string): string;
  persistUnmatchedApprovalTerminal(
    conversationId: string,
    approvalResponse: ApprovalResponse,
    errorText: string,
  ): Promise<void>;
  /** Resolve site URL for artifact entity-ref parsing (denial check). */
  displayBaseUrl: string | undefined;
  /** Backs the permission-denied artifact check so restricted cards are not streamed. */
  entityService: {
    getEntity: (ref: {
      entityType: string;
      id: string;
      // The scope the entity service actually accepts. Declared here rather
      // than as `unknown`, which forced the caller to assert the ref back into
      // the real request type.
      visibilityScope?: "public" | "shared" | "restricted" | undefined;
    }) => Promise<MessageArtifactEntity | null | undefined>;
  };
}

async function deniedArtifactCardIds(
  deps: StreamDeps,
  response: Pick<AgentResponse, "cards">,
  userLevel: UserPermissionLevel,
): Promise<Set<string>> {
  return collectDeniedArtifactCardIds({
    cards: response.cards,
    userLevel,
    displayBaseUrl: deps.displayBaseUrl,
    getEntity: (ref) => deps.entityService.getEntity(ref),
    getVisibleEntity: (ref, visibilityScope) =>
      deps.entityService.getEntity({ ...ref, visibilityScope }),
  });
}

interface WebChatPrincipalAttribution {
  userId: string;
  canonicalId?: string;
  displayName: string;
  isAnchor: boolean;
}

function hasMatchingApprovalCard(
  response: Pick<AgentResponse, "cards">,
  approvalResponse: ApprovalResponse,
): boolean {
  return Boolean(
    response.cards?.some(
      (card) =>
        card.kind === "tool-approval" &&
        (card.id === approvalResponse.id ||
          (approvalResponse.toolCallId !== undefined &&
            card.toolCallId === approvalResponse.toolCallId)),
    ),
  );
}

async function writeUnmatchedApprovalTerminal(
  writer: StreamWriter,
  conversationId: string,
  approvalResponse: ApprovalResponse,
  response: Pick<AgentResponse, "cards" | "text" | "error">,
  deps: StreamDeps,
): Promise<void> {
  if (hasMatchingApprovalCard(response, approvalResponse)) return;

  // AI SDK automatically resubmits a trailing approval response until its
  // tool part reaches a terminal state. A stale server-side approval has no
  // result card, so close the client-side tool instead of replaying forever.
  const responseText =
    response.error !== undefined
      ? "The action failed."
      : stripInternalEntityMemoryNote(response.text).trim();
  const errorText = responseText || "This approval is no longer pending.";
  writer.write({
    type: "tool-output-error",
    toolCallId: approvalResponse.toolCallId ?? approvalResponse.id,
    errorText,
    dynamic: true,
  });
  await deps.persistUnmatchedApprovalTerminal(
    conversationId,
    approvalResponse,
    errorText,
  );
}

interface StreamedChatInput {
  writer: StreamWriter;
  conversationId: string;
  message: string;
  permissionLevel: UserPermissionLevel;
  principal?: WebChatPrincipalAttribution;
  attachments: ChatAttachment[];
  messageId?: string;
  interfaceType: string;
  signal?: AbortSignal;
}

export async function handleStreamedChat(
  input: StreamedChatInput,
  deps: StreamDeps,
): Promise<void> {
  deps.activeStreams.set(input.conversationId, { writer: input.writer });
  deps.startProcessingInput(input.conversationId);

  try {
    const response = await deps.agent.chat(
      input.message,
      input.conversationId,
      {
        ...buildWebChatContext(input),
        attachments: input.attachments,
      },
      input.signal,
    );

    await deps.handleAgentResponseToolStatuses(response, input.conversationId);
    if (response.error !== undefined) throw new Error(response.error);
    const deniedCardIds = await deniedArtifactCardIds(
      deps,
      response,
      input.permissionLevel,
    );
    const plan = buildResponsePlan(response, { deniedCardIds });
    writeText(input.writer, response.text, "text", deps.createId);
    for (const toolResult of response.toolResults ?? []) {
      input.writer.write({
        type: "data-tool-result",
        id: deps.createId("tool"),
        data: redactUploadRefs(toolResult),
      });
    }
    writePlanCards(input.writer, plan);
  } finally {
    deps.endProcessingInput();
    deps.activeStreams.delete(input.conversationId);
  }
}

interface StreamedConfirmationsInput {
  writer: StreamWriter;
  conversationId: string;
  approvalResponses: ApprovalResponse[];
  permissionLevel: UserPermissionLevel;
  principal?: WebChatPrincipalAttribution;
  interfaceType: string;
  signal?: AbortSignal;
}

export async function handleStreamedConfirmations(
  input: StreamedConfirmationsInput,
  deps: StreamDeps,
): Promise<void> {
  deps.activeStreams.set(input.conversationId, { writer: input.writer });
  deps.startProcessingInput(input.conversationId);

  try {
    let failure: string | undefined;
    for (const approvalResponse of input.approvalResponses) {
      const response = await deps.agent.confirmPendingAction(
        input.conversationId,
        approvalResponse.approved,
        approvalResponse.id,
        {
          ...buildWebChatContext(input, {
            trigger: "approval-response",
            approvalId: approvalResponse.id,
          }),
        },
        input.signal,
      );
      await deps.handleAgentResponseToolStatuses(
        response,
        input.conversationId,
      );
      const deniedCardIds = await deniedArtifactCardIds(
        deps,
        response,
        input.permissionLevel,
      );
      const plan = buildResponsePlan(response, { deniedCardIds });
      if (response.error !== undefined) failure = response.error;
      else writeText(input.writer, response.text, "text", deps.createId);
      writePlanCards(input.writer, plan);
      await writeUnmatchedApprovalTerminal(
        input.writer,
        input.conversationId,
        approvalResponse,
        response,
        deps,
      );
    }
    // Finish the explicitly submitted decisions without replaying any of them,
    // then let the SDK report a sanitized stream error rather than success.
    if (failure !== undefined) throw new Error(failure);
  } finally {
    deps.endProcessingInput();
    deps.activeStreams.delete(input.conversationId);
  }
}

function buildWebChatContext(
  input: {
    conversationId: string;
    interfaceType: string;
    permissionLevel: UserPermissionLevel;
    principal?: WebChatPrincipalAttribution;
    messageId?: string;
  },
  metadata: Record<string, unknown> = { trigger: "message" },
): ChatContext {
  return {
    userPermissionLevel: input.permissionLevel,
    isAnchor: input.principal?.isAnchor ?? false,
    interfaceType: input.interfaceType,
    channelId: input.conversationId,
    channelName: "Web Chat",
    actor: buildMessageActorMetadata({
      identity: input.principal
        ? {
            kind: "user",
            userId: input.principal.userId,
            ...(input.principal.canonicalId
              ? { canonicalId: input.principal.canonicalId }
              : {}),
          }
        : {
            kind: "external",
            externalActorId: createExternalActorId(
              input.interfaceType,
              `${input.interfaceType}:${input.conversationId}:browser-user`,
            ),
          },
      interfaceType: input.interfaceType,
      role: "user",
      displayName: input.principal?.displayName ?? "Web Chat user",
    }),
    source: buildMessageSourceMetadata({
      ...(input.messageId ? { messageId: input.messageId } : {}),
      channelId: input.conversationId,
      channelName: "Web Chat",
      metadata,
    }),
  };
}

export function writeText(
  writer: StreamWriter,
  text: string,
  prefix: string,
  createId: (prefix: string) => string,
): string {
  const id = createId(prefix);
  writeTextPart(writer, id, stripInternalEntityMemoryNote(text));
  return id;
}
